import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Box,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Link,
    Skeleton,
    Alert,
    Divider
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ErrorOutline as ErrorIcon,
    Security as SecurityIcon,
    BugReport as BugIcon,
    Info as InfoIcon,
} from '@mui/icons-material';

// eslint-disable-next-line no-unused-vars
const DetailSection = ({ title, children }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
            {title}
        </Typography>
        {children}
    </Box>
);

const getSeverityColor = (severity) => {
    switch (severity) {
        case 4: return 'error'; // Critical
        case 3: return 'error'; // High
        case 2: return 'warning'; // Medium
        case 1: return 'info'; // Low
        case 0: return 'default'; // Info
        default: return 'default';
    }
};

const getSeverityLabel = (severity) => {
    switch (severity) {
        case 4: return 'Critical';
        case 3: return 'High';
        case 2: return 'Medium';
        case 1: return 'Low';
        case 0: return 'Info';
        default: return 'Info';
    }
};

const VulnerabilityDetails = ({ vulnerability, loading, error }) => {
    const [expanded, setExpanded] = useState('description');

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const renderHtml = (content) => {
        if (!content) return null;
        return <div dangerouslySetInnerHTML={{ __html: content }} />;
    };

    const renderCvssDetails = () => {
        if (!vulnerability?.cvss2 && !vulnerability?.cvss3 && !vulnerability?.cvss4) return null;

        return (
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>CVSS Scores</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {vulnerability.cvss2 && (
                        <Chip
                            label={`CVSS v2: ${vulnerability.cvss2}`}
                            variant="outlined"
                            size="small"
                        />
                    )}
                    {vulnerability.cvss3 && (
                        <Chip
                            label={`CVSS v3: ${vulnerability.cvss3}`}
                            variant="outlined"
                            size="small"
                        />
                    )}
                    {vulnerability.cvss4 && (
                        <Chip
                            label={`CVSS v4: ${vulnerability.cvss4}`}
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>
            </Box>
        );
    };

    const renderTechnicalDetails = () => {
        const details = vulnerability?.details?.details;
        if (!details) return null;

        return (
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Technical Details</Typography>
                <Paper 
                    variant="outlined" 
                    sx={{ 
                        p: 2,
                        bgcolor: 'background.paper',
                        '& ul': { ml: 2 },
                        '& li': { mb: 1 }
                    }}
                >
                    {renderHtml(details)}
                </Paper>
            </Box>
        );
    };

    const renderReferenceUrl = (reference, index) => {
        if (!reference?.href) return null;
        
        return (
            <Box 
                component="li" 
                key={index}
                sx={{
                    mb: 2,
                    pl: 2,
                    borderLeft: '2px solid',
                    borderLeftColor: 'primary.main',
                    '&:hover': {
                        borderLeftColor: 'secondary.main'
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Link
                        href={reference.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        {reference.rel || reference.href}
                    </Link>
                    <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ wordBreak: 'break-all' }}
                    >
                        {reference.href}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const renderReferences = () => {
        const refs = vulnerability?.details?.references || vulnerability?.references;
        if (!refs || refs.length === 0) return null;

        return (
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>References</Typography>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                    {refs.map((ref, index) => renderReferenceUrl(ref, index))}
                </Box>
            </Box>
        );
    };

    const renderAffectedUrl = () => {
        if (!vulnerability?.affects_url) return null;

        return (
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Affected URL
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Link 
                        href={vulnerability.affects_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ wordBreak: 'break-all' }}
                    >
                        {vulnerability.affects_url}
                    </Link>
                    {vulnerability.affects_detail && (
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ wordBreak: 'break-all' }}
                        >
                            Path: {vulnerability.affects_detail}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    };

    if (loading) {
        return (
            <Paper sx={{ p: 3 }}>
                <Skeleton variant="text" height={40} width="60%" />
                <Skeleton variant="text" height={24} width="30%" />
                <Skeleton variant="rectangular" height={100} sx={{ mt: 2 }} />
            </Paper>
        );
    }

    if (error) {
        return (
            <Alert severity="error" icon={<ErrorIcon />} sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!vulnerability) {
        return (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
                Select a vulnerability to view details
            </Alert>
        );
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon 
                    color={getSeverityColor(vulnerability.severity)} 
                    sx={{ mr: 2, fontSize: 32 }} 
                />
                <Typography variant="h5" component="h2">
                    {vulnerability.vt_name || 'Unnamed Vulnerability'}
                </Typography>
            </Box>

            <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                    label={`Severity: ${getSeverityLabel(vulnerability.severity)}`}
                    color={getSeverityColor(vulnerability.severity)}
                />
                <Chip
                    icon={<BugIcon />}
                    label={`Confidence: ${vulnerability.confidence}%`}
                    variant="outlined"
                />
                {vulnerability.status && (
                    <Chip
                        label={`Status: ${vulnerability.status}`}
                        variant="outlined"
                    />
                )}
            </Box>

            {renderAffectedUrl()}
            {renderCvssDetails()}
            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {vulnerability.description && (
                    <Accordion
                        expanded={expanded === 'description'}
                        onChange={handleAccordionChange('description')}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Description</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {renderHtml(vulnerability.description)}
                        </AccordionDetails>
                    </Accordion>
                )}

                {vulnerability.impact && (
                    <Accordion
                        expanded={expanded === 'impact'}
                        onChange={handleAccordionChange('impact')}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Impact</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {renderHtml(vulnerability.impact)}
                        </AccordionDetails>
                    </Accordion>
                )}

                {vulnerability.request && (
                    <Accordion
                        expanded={expanded === 'technical'}
                        onChange={handleAccordionChange('technical')}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Technical Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Request Information
                                </Typography>
                                <Paper 
                                    variant="outlined" 
                                    sx={{ 
                                        p: 2, 
                                        backgroundColor: 'background.default',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        overflowX: 'auto'
                                    }}
                                >
                                    {vulnerability.request}
                                </Paper>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                )}

                {vulnerability.recommendation && (
                    <Accordion
                        expanded={expanded === 'remediation'}
                        onChange={handleAccordionChange('remediation')}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6">Remediation</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {renderHtml(vulnerability.recommendation)}
                        </AccordionDetails>
                    </Accordion>
                )}

                {renderTechnicalDetails()}
                {renderReferences()}
            </Box>
        </Paper>
    );
};

export default VulnerabilityDetails;
